import React, { useState } from 'react'
import { pageMeta } from '../../../access/pageMeta'
import { Button, EmptyState } from '../../../ui'
import type { Visit } from './types'
import { dayKey, formatDay, formatDwell, formatTime } from './format'
import { VisitCalls } from './VisitCalls'
import styles from './styles.module.scss'

interface TimelineProps {
    cognitoId: string
    visits: Visit[]
    loading: boolean
    error: string | null
    total: number
    truncated: boolean
    onLoadMore: () => void
}

const visitKey = (visit: Visit) => `${visit.at}|${visit.page || ''}`

const pageName = (page: string | null) => {
    if (!page) return 'Unknown page'
    return pageMeta(page).name
}

/**
 * One row per page visit, grouped under day headings. The row shows only
 * what the user would recognise: time, page, what they searched/opened, and
 * IP. Expanding a row loads the API calls behind it.
 */
export const Timeline = ({ cognitoId, visits, loading, error, total, truncated, onLoadMore }: TimelineProps) => {
    const [expanded, setExpanded] = useState<string | null>(null)

    if (error) return <EmptyState tone="error" title="Could not load activity" description={error} />
    if (loading && visits.length === 0) return <EmptyState tone="loading" title="Loading activity…" />
    if (!loading && visits.length === 0) {
        return (
            <EmptyState
                title="No activity in this range"
                description="Nothing has been recorded for this user in the selected period."
            />
        )
    }

    let lastDay: string | null = null

    return (
        <div className={styles.timeline}>
            {visits.map((visit) => {
                const key = visitKey(visit)
                const day = dayKey(visit.at)
                const heading = day !== lastDay ? <div className={styles.day}>{formatDay(visit.at)}</div> : null
                lastDay = day
                const isOpen = expanded === key
                const dwell = formatDwell(visit.at, visit.until)
                const { summary } = visit
                return (
                    <React.Fragment key={key}>
                        {heading}
                        <div className={styles.visit}>
                            <button
                                type="button"
                                className={styles.visitRow}
                                aria-expanded={isOpen}
                                onClick={() => setExpanded(isOpen ? null : key)}
                            >
                                <span className={styles.time}>{formatTime(visit.at)}</span>
                                <span className={styles.page}>
                                    <strong>
                                        {pageName(visit.page)}
                                        {visit.implicit && (
                                            <span className={styles.implicit} title="No page-view event; inferred from API calls">
                                                {' '}
                                                (inferred)
                                            </span>
                                        )}
                                    </strong>
                                    {visit.path && visit.path !== visit.page && <span title={visit.path}>{visit.path}</span>}
                                </span>
                                <span className={styles.summary}>
                                    {summary.searches.slice(0, 3).map((s) => (
                                        <span key={s.value} className={styles.searchChip} title={`Searched "${s.value}"`}>
                                            Searched “{s.value}”
                                            {s.results != null && (
                                                <em>
                                                    · {s.results} result{s.results === 1 ? '' : 's'}
                                                </em>
                                            )}
                                        </span>
                                    ))}
                                    {summary.searches.length > 3 && (
                                        <span className={styles.muted}>+{summary.searches.length - 3} more</span>
                                    )}
                                    {summary.searches.length === 0 && summary.opened && (
                                        <span className={styles.opened}>Opened {summary.opened}</span>
                                    )}
                                    {summary.actions > 0 && (
                                        <span className={styles.actions}>
                                            {summary.actions} action{summary.actions === 1 ? '' : 's'}
                                        </span>
                                    )}
                                    {summary.errors > 0 && (
                                        <span className={styles.errors}>
                                            {summary.errors} error{summary.errors === 1 ? '' : 's'}
                                        </span>
                                    )}
                                </span>
                                <span className={styles.dwell} title="Time on page">
                                    {dwell || ''}
                                </span>
                                <span className={styles.ip}>{visit.ip || '—'}</span>
                            </button>
                            {isOpen && <VisitCalls cognitoId={cognitoId} visit={visit} />}
                        </div>
                    </React.Fragment>
                )
            })}
            {(visits.length < total || truncated) && (
                <div className={styles.footer}>
                    {visits.length < total ? (
                        <Button size="sm" onClick={onLoadMore} loading={loading}>
                            Show more ({total - visits.length} remaining)
                        </Button>
                    ) : (
                        <span className={styles.muted}>
                            Range too large to show in full; narrow the time range to see everything.
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}
